import React, { useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey, Transaction } from '@solana/web3.js';
import { 
  getAssociatedTokenAddressSync, 
  createTransferInstruction, 
  createAssociatedTokenAccountInstruction 
} from '@solana/spl-token';
import { api, User } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

// Pump.fun tokens have 6 decimals
const SOLEMATE_MINT_STR = 'PLACEHOLDER_PUMP_FUN_MINT_ADDRESS';
const DECIMALS = 6;

interface TipModalProps {
  isOpen: boolean;
  onClose: () => void;
  creator: User;
  onTipSuccess?: (amount: number) => void;
}

export const TipModal: React.FC<TipModalProps> = ({ isOpen, onClose, creator, onTipSuccess }) => {
  const { connection } = useConnection();
  const { publicKey, sendTransaction } = useWallet();
  
  const [amount, setAmount] = useState<string>('50');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const quickAmounts = ['10', '50', '100', '500'];

  const handleSendTip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicKey) {
      setError('Please connect your wallet first.');
      return;
    }
    if (!creator.tip_wallet) {
      setError('This creator has not configured their tip wallet.');
      return;
    }

    const tipAmount = parseFloat(amount);
    if (isNaN(tipAmount) || tipAmount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const mint = new PublicKey(SOLEMATE_MINT_STR);
      const fromWallet = publicKey;
      const toWallet = new PublicKey(creator.tip_wallet);

      // Derive Associated Token Accounts
      const fromATA = getAssociatedTokenAddressSync(mint, fromWallet);
      const toATA = getAssociatedTokenAddressSync(mint, toWallet);

      const transaction = new Transaction();

      // Check if destination ATA exists
      const accountInfo = await connection.getAccountInfo(toATA);
      if (!accountInfo) {
        // If not, add create instruction
        transaction.add(
          createAssociatedTokenAccountInstruction(
            fromWallet, // payer
            toATA,      // ata address
            toWallet,   // owner
            mint        // token mint
          )
        );
      }

      // Add transfer instruction (Pump.fun tokens have 6 decimals)
      const rawAmount = Math.round(tipAmount * Math.pow(10, DECIMALS));
      transaction.add(
        createTransferInstruction(
          fromATA,
          toATA,
          fromWallet,
          rawAmount
        )
      );

      // Send transaction via wallet adapter
      const signature = await sendTransaction(transaction, connection);
      
      // Wait for confirmation
      const latestBlockhash = await connection.getLatestBlockhash();
      await connection.confirmTransaction({
        signature,
        ...latestBlockhash
      }, 'confirmed');

      // Submit signature to backend
      await api.tipCreator({
        tx_sig: signature,
        to_creator_id: creator.id,
        message: message.trim() || undefined
      });

      setSuccess(true);
      if (onTipSuccess) onTipSuccess(tipAmount);
      
      setTimeout(() => {
        setSuccess(false);
        setAmount('50');
        setMessage('');
        onClose();
      }, 2500);

    } catch (err: any) {
      console.error('Tipping transaction failed', err);
      setError(err.message || 'Transaction failed. Please make sure you hold enough $SOLEM8 and SOL for fees.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay" onClick={onClose}>
          <motion.div 
            className="modal-content glass-card"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
          >
            <button className="modal-close" onClick={onClose}>&times;</button>

            {success ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🦶💸✨</div>
                <h2>Tip Sent Successfully!</h2>
                <p style={{ marginTop: '0.5rem' }}>Your support has been recorded on-chain.</p>
              </div>
            ) : (
              <form onSubmit={handleSendTip}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '0.25rem', fontFamily: 'var(--font-heading)' }}>
                  Tip {creator.display_name || creator.username || 'Creator'} 🦶
                </h3>
                <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                  Send $SOLEM8 tokens directly to their tip wallet.
                </p>

                {error && (
                  <div style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgba(239, 68, 68, 0.2)', 
                    color: '#ef4444', 
                    padding: '0.8rem 1rem', 
                    borderRadius: 'var(--radius-md)', 
                    fontSize: '0.85rem', 
                    marginBottom: '1.25rem' 
                  }}>
                    ⚠️ {error}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Amount ($SOLEM8)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="1"
                    step="any"
                    required
                    disabled={loading}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  {quickAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className={`btn btn-sm ${amount === amt ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex: 1 }}
                      onClick={() => setAmount(amt)}
                      disabled={loading}
                    >
                      {amt}
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label className="form-label">Message (Optional)</label>
                  <textarea 
                    className="form-input" 
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Add a sweet message..."
                    rows={3}
                    style={{ resize: 'none' }}
                    disabled={loading}
                  />
                </div>

                <button 
                  type="submit" 
                  className={`btn btn-primary ${loading ? 'btn-disabled shimmer' : ''}`}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                  disabled={loading}
                >
                  {loading ? 'Sending transaction...' : 'Send Tip 🦶💸'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
